import React from 'react'
import ReactDOM from 'react-dom'
import { LexicalTypeaheadMenuPlugin } from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import cx from 'classnames'
import * as R from 'ramda'

import { $createTextVariableNode } from 'components/shared/FormFields/common/Editors/nodes/TextVariableNode/TextVariableNode'
import { useVariableLookup } from './hooks'
import { regex } from './model'

import styles from './TextVariablePlugin.module.scss'

const checkForVariableSign = text => {
  let match = regex.exec(text)

  if (match !== null) {
    const matchingString = match[3]

    return {
      leadOffset: match.index,
      matchingString,
      replaceableString: match[2],
    }
  }

  return null
}

class VariableTypeaheadOption {
  constructor(key, label) {
    this.key = key
    this.label = label
  }
}

const VariableTypeheadMenuItem = ({
  index,
  option: { key, label, setRefElement },
  onClick,
  onMouseEnter,
  isSelected,
}) => (
  <li
    aria-selected={isSelected}
    className={cx(styles.item, { [styles.selected]: isSelected })}
    id={`item-${index}`}
    key={key}
    ref={setRefElement}
    role="option"
    tabIndex={-1}
  >
    <button className={styles.button} {...{ onClick, onMouseEnter }}>
      {label}
    </button>
  </li>
)

export const TextVariablePlugin = ({ variables }) => {
  const [editor] = useLexicalComposerContext()

  const [query, setQuery] = React.useState(null)

  const results = useVariableLookup(query, variables)

  const options = React.useMemo(
    () =>
      R.map(
        ({ key, label }) => new VariableTypeaheadOption(key, label),
        results,
      ),
    [results],
  )

  const onSelectOption = React.useCallback(
    (selectedOption, nodeToReplace, closeMenu) => {
      editor.update(() => {
        const node = $createTextVariableNode(
          selectedOption?.key,
          selectedOption?.label,
        )

        if (nodeToReplace) {
          nodeToReplace.replace(node)
        }

        node.select()
        closeMenu()
      })
    },
    [editor],
  )

  return (
    <LexicalTypeaheadMenuPlugin
      onQueryChange={setQuery}
      onSelectOption={onSelectOption}
      triggerFn={checkForVariableSign}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) =>
        anchorElementRef.current && results.length
          ? ReactDOM.createPortal(
              <div className={styles.typehead}>
                <menu className={styles.menu}>
                  {options.map((option, i) => (
                    <VariableTypeheadMenuItem
                      index={i}
                      key={option.key}
                      onClick={() => {
                        setHighlightedIndex(i)
                        selectOptionAndCleanUp(option)
                      }}
                      onMouseEnter={() => {
                        setHighlightedIndex(i)
                      }}
                      isSelected={selectedIndex === i}
                      {...{ option }}
                    />
                  ))}
                </menu>
              </div>,
              anchorElementRef.current,
            )
          : null
      }
      {...{ options }}
    />
  )
}
